import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useMemo, useState } from 'react'

import type { BranchMenu } from '@/bindings/BranchMenu'
import type { BranchMenuItem } from '@/bindings/BranchMenuItem'
import type { Currency } from '@/bindings/Currency'
import type { Language } from '@/bindings/Language'
import type { Menu } from '@/bindings/Menu'
import type { MenuCategory } from '@/bindings/MenuCategory'
import type { MenuItem } from '@/bindings/MenuItem'
import type { SetBranchAvailabilityRequest } from '@/bindings/SetBranchAvailabilityRequest'
import type { SetBranchPriceRequest } from '@/bindings/SetBranchPriceRequest'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Checkbox, Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, branchMenuPath, branchMenuQuery, menuQuery } from '@/features/back-office/api'
import { MAX_STOCK, groupByCategory, priceStep } from '@/features/back-office/menu-format'
import { STEP_LABELS, formatPrice } from '@/lib/menu-format'
import { claim, request } from '@/lib/api'
import { minorUnits } from '@/lib/currencies'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { canManageBranch, canOverridePrice } from '@/lib/roles'


/** What the rows and forms need from the scope. */
interface BranchContext {
  restaurantId: string
  branchId: string
  main: Language
  currency: Currency
  /** The branch currency's minor units: the override's decimal allowance. */
  decimals: number
  /** Manager and above (PRD): the price field is read-only below that. */
  canPrice: boolean
}

const DEFAULT_OVERRIDE = { available: true, stock: null, price: null } as const

/**
 * Branch menu page (PRD, F4): the restaurant menu as one branch serves it. Admin and above
 * in the branch set availability and daily stock, manager and above a price. Shares F3's cached `Menu`.
 */
export function BranchMenuPage() {
  const t = useT()
  const { restaurantId, branchId, context } = useManagedBranch()
  const allowed = context !== undefined && canManageBranch(context.role)
  const menu = useQuery({ ...menuQuery(restaurantId), enabled: allowed })
  const overlay = useQuery({ ...branchMenuQuery(restaurantId, branchId), enabled: allowed })

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('branchMenu.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (!allowed) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('branchMenu.adminOnly')}
        </p>
      </Page>
    )
  }

  const error = menu.error ?? overlay.error
  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <p className="text-muted text-sm">{t('branchMenu.hint')}</p>
      {(menu.isPending || overlay.isPending) && !error && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {error && (
        <div className="flex flex-col gap-2">
          <FormError error={error} />
          <Button
            variant="secondary"
            onClick={() => {
              if (menu.error) void menu.refetch()
              if (overlay.error) void overlay.refetch()
            }}
            className="self-start"
          >
            {t('common.retry')}
          </Button>
        </div>
      )}
      {menu.data && overlay.data && (
        <BranchMenuEditor
          menu={menu.data}
          overlay={overlay.data}
          context={{
            restaurantId,
            branchId,
            main: context.settings.main_language,
            currency: overlay.data.branch.currency,
            decimals: minorUnits(overlay.data.branch.currency),
            canPrice: canOverridePrice(context.role),
          }}
        />
      )}
    </Page>
  )
}

/** Patches the cached overlay from a mutation's response; nothing refetches on its own. */
function useOverlayPatch({ restaurantId, branchId }: BranchContext) {
  const queryClient = useQueryClient()
  return (saved: BranchMenuItem) => {
    queryClient.setQueryData(backOfficeKeys.branchMenu(restaurantId, branchId), (current: BranchMenu | undefined) => {
      if (!current) return current
      const known = current.items.some((item) => item.item_id === saved.item_id)
      return {
        ...current,
        items: known ? current.items.map((item) => (item.item_id === saved.item_id ? saved : item)) : [...current.items, saved],
      }
    })
  }
}

function BranchMenuEditor({ menu, overlay, context }: { menu: Menu; overlay: BranchMenu; context: BranchContext }) {
  const t = useT()
  const itemsByCategory = useMemo(() => groupByCategory(menu), [menu])
  const overrides = useMemo(() => new Map(overlay.items.map((item) => [item.item_id, item])), [overlay])

  if (menu.items.length === 0) return <p className="text-muted text-sm">{t('branchMenu.noMenu')}</p>
  return (
    <>
      {menu.categories.map((category) => (
        <CategoryCard
          key={category.id}
          context={context}
          category={category}
          items={itemsByCategory.get(category.id) ?? []}
          overrides={overrides}
        />
      ))}
    </>
  )
}

function CategoryCard({
  context,
  category,
  items,
  overrides,
}: {
  context: BranchContext
  category: MenuCategory
  items: readonly MenuItem[]
  overrides: Map<string, BranchMenuItem>
}) {
  const t = useT()
  const name = useLocalizedText(context.main)
  const [editing, setEditing] = useState<string | undefined>()
  const [saved, setSaved] = useState(false)
  const heading = name(category.name)

  return (
    <Card className="flex flex-col gap-4" aria-label={heading}>
      <h2 className="text-lg font-semibold">{heading}</h2>
      {saved && (
        <p role="status" className="text-muted text-sm">
          {t('branchMenu.saved')}
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-muted text-sm">{t('menu.noItems')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const override = overrides.get(item.id) ?? { item_id: item.id, ...DEFAULT_OVERRIDE }
            return (
              <li key={item.id} className="border-border flex flex-col gap-3 rounded-lg border p-3">
                {editing === item.id ? (
                  <OverrideForm
                    context={context}
                    item={item}
                    override={override}
                    onSaved={() => {
                      setEditing(undefined)
                      setSaved(true)
                    }}
                    onCancel={() => setEditing(undefined)}
                  />
                ) : (
                  <ItemRow
                    context={context}
                    item={item}
                    override={override}
                    onEdit={() => {
                      setSaved(false)
                      setEditing(item.id)
                    }}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

function ItemRow({
  context,
  item,
  override,
  onEdit,
}: {
  context: BranchContext
  item: MenuItem
  override: BranchMenuItem
  onEdit: () => void
}) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(context.main)
  const patch = useOverlayPatch(context)
  const label = name(item.name)
  const price = (value: string) => formatPrice(value, context.decimals, language)

  // The one-tap lever for "sold out": availability flips in place; the stock is not sent, so it stays.
  const toggle = useMutation({
    mutationFn: (available: boolean) => {
      const body: SetBranchAvailabilityRequest = { available }
      return request<BranchMenuItem>(branchMenuPath(context.restaurantId, context.branchId, `/items/${item.id}/availability`), {
        method: 'PUT',
        body,
      })
    },
    onSuccess: patch,
  })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        {item.image_url ? (
          <img src={item.image_url} alt={t('menu.pictureOf', { name: label })} className="size-16 shrink-0 rounded-lg object-cover" />
        ) : (
          <span aria-hidden className="bg-accent-soft size-16 shrink-0 rounded-lg" />
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate font-medium">{label}</span>
          <span className="text-muted flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span>{price(override.price ?? item.price)}</span>
            {override.price !== null && <span>{t('branchMenu.basePrice', { price: price(item.price) })}</span>}
            {item.step !== '1' && <span>{t(STEP_LABELS[item.step])}</span>}
          </span>
          <span className="flex flex-wrap gap-1">
            {!override.available && <Badge tone="warning">{t('branchMenu.unavailable')}</Badge>}
            {override.available && override.stock === 0 && <Badge tone="warning">{t('branchMenu.soldOut')}</Badge>}
            {override.stock !== null && override.stock > 0 && (
              <Badge tone="muted">{t('branchMenu.stockLeft', { count: override.stock })}</Badge>
            )}
          </span>
        </span>
        <span className="flex flex-col items-end gap-1">
          <Checkbox
            label={t('branchMenu.available')}
            aria-label={t('branchMenu.availableFor', { name: label })}
            checked={override.available}
            disabled={toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
            className="text-sm"
          />
          <Button size="sm" variant="secondary" aria-label={`${t('branchMenu.edit')}: ${label}`} onClick={onEdit}>
            {t('common.edit')}
          </Button>
        </span>
      </div>
      <FormError error={toggle.error} />
    </div>
  )
}

/**
 * Stock and branch price for one item, two endpoints behind one form. Only what changed is
 * sent (a stale field never overwrites a live one); a price refusal keeps the form open.
 */
function OverrideForm({
  context,
  item,
  override,
  onSaved,
  onCancel,
}: {
  context: BranchContext
  item: MenuItem
  override: BranchMenuItem
  onSaved: () => void
  onCancel: () => void
}) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(context.main)
  const patch = useOverlayPatch(context)
  const [stock, setStock] = useState(override.stock === null ? '' : String(override.stock))
  const [price, setPrice] = useState(override.price ?? '')
  const label = name(item.name)
  const base = branchMenuPath(context.restaurantId, context.branchId, `/items/${item.id}`)

  const save = useMutation({
    mutationFn: async () => {
      const nextStock = stock.trim() === '' ? null : Number(stock)
      if (nextStock !== override.stock) {
        const body: SetBranchAvailabilityRequest = { stock: nextStock }
        patch(await request<BranchMenuItem>(`${base}/availability`, { method: 'PUT', body }))
      }
      const nextPrice = price.trim() === '' ? null : price.trim()
      if (context.canPrice && nextPrice !== override.price) {
        const body: SetBranchPriceRequest = { price: nextPrice }
        patch(await request<BranchMenuItem>(`${base}/price`, { method: 'PUT', body }))
      }
    },
    onSuccess: onSaved,
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const stockError = claim(save.error, 'INVALID_STOCK')
  const priceError = claim(save.error, 'INVALID_PRICE')
  const basePrice = formatPrice(item.price, context.decimals, language)
  const priceHint = context.canPrice
    ? `${t('branchMenu.priceHint', { price: basePrice })} ${t('menu.priceIn', { currencies: context.currency })} ${
        context.decimals === 0 ? t('menu.priceWhole') : t('menu.priceDecimals', { decimals: context.decimals })
      }`
    : t('branchMenu.priceManagerOnly')

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label={`${t('branchMenu.edit')}: ${label}`}>
      <p className="font-medium">{label}</p>
      <Field label={t('branchMenu.stock')} hint={t('branchMenu.stockHint')} error={stockError}>
        <Input
          type="number"
          inputMode="numeric"
          min="0"
          max={MAX_STOCK}
          step="1"
          value={stock}
          onChange={(e) => {
            if (save.error) save.reset()
            setStock(e.target.value)
          }}
          autoFocus
        />
      </Field>
      <Field label={t('branchMenu.price')} hint={priceHint} error={priceError}>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step={priceStep(context.decimals)}
          value={price}
          disabled={!context.canPrice}
          onChange={(e) => {
            if (save.error) save.reset()
            setPrice(e.target.value)
          }}
        />
      </Field>
      <FormError error={stockError || priceError ? undefined : save.error} />
      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
